import styled, { css } from "styled-components";
import { Icon as MaterialIcon } from "@material-ui/core";
import React from "react";
import { Button } from "js/Components/Button";
import { themeColors } from "js/react/sharedStyles";
import { Input } from "js/Components/Input";
import type StripeTypes from "stripe";
import { mediumText } from "js/textStyles";
import { FlexBox } from "js/react/components/LayoutGrid";
import { usePromoFormLogic, IPromoFormProps } from "js/react/views/Billing/Checkout/components/PromoForm/usePromoFormLogic";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { BlockHeader } from "js/react/views/UserOptions/panes/BillingPane/Components/Containers";
import { BillingPromoTermsMessage } from "./BillingPromoTermsMessage";

const BillingPromoTerms = styled.span`
    ${mediumText}
`;

const BillingPromoTermsLink = styled.a`
    ${mediumText}
    text-decoration: none;
    color: ${themeColors.ui_blue};
`;

const BillingPromoInput = styled(Input)`
    display: flex;
    width: 220px;
    padding: 10px;
    align-items: center;
    gap: 10px;
`;

const BillingPromoError = styled.div`
    ${mediumText}
    color: #FB0000;
`;

const PromoInputContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const PromoCodeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

// Billing-specific promo form component
export const BillingPromoForm = (props: IPromoFormProps) => {
    const {
        isAddingPromo,
        isApplyingPromo,
        promoError,
        promoCode,
        handleAddPromo,
        handleChangePromoCode,
        handleApplyPromo
    } = usePromoFormLogic(props.verifyPromoCode, props.initialPromotionCode, props.prefillOnly);

    return (
        <FlexBox fillWidth horizontalAlign="between" verticalAlign="top" data-test-id="promo-container">
            <PromoCodeContainer>
                <BlockHeader>Promo Code</BlockHeader>
                {!isAddingPromo ? <BillingPromoTerms>&nbsp;</BillingPromoTerms> : <PromoInputContainer>
                    <FetchingClickShield visible={isApplyingPromo} backgroundColor="rgba(255,255,255,0.7)"/>
                    <BillingPromoInput
                        width="100%"
                        value={promoCode}
                        onChange={handleChangePromoCode}
                        onKeyPress={e => {
                            if (e.key === "Enter" && promoCode && !promoError && !isApplyingPromo) {
                                handleApplyPromo();
                            }
                        }}
                        placeholder="Add Code"
                    />
                </PromoInputContainer>}
                {isAddingPromo && !promoError && <BillingPromoTerms>
                    <BillingPromoTermsMessage />
                </BillingPromoTerms>}
                {promoError && <BillingPromoError>{promoError}</BillingPromoError>}
            </PromoCodeContainer>
            <div>
                {!isAddingPromo ? <Button unframed large blue onClick={handleAddPromo}>add promo code</Button>
                    : <Button unframed large blue onClick={handleApplyPromo}
                        disabled={!promoCode || promoError || isApplyingPromo}>apply</Button>}
            </div>
        </FlexBox>
    );
};

// Styled components for billing promo coupon
const StyledBillingPromoCoupon = styled.div`
    display: flex;
    color: ${themeColors.ui_blue};
    gap: 5px;
    padding: 8px 10px;
    align-items: center;
    border-radius: 2px;
    background: #D6EFF9;
    color: ${themeColors.ui_blue};
`;

const PromoCode = css`
    color: #11A9E2;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 125%; /* 17.5px */
`;

const PromoCodeDetails = styled.span`
    ${PromoCode}
`;

const PromoCodeText = styled.span`
    ${PromoCode}
    text-transform: uppercase;
`;

// Billing-specific promo coupon component
export const BillingPromoCoupon = (props: {coupon: StripeTypes.Coupon}) => {
    const { coupon } = props;

    return (
        <StyledBillingPromoCoupon data-test-id="promo-container" >
            <MaterialIcon style={{ fontSize: 18 }}>percent</MaterialIcon>
            <div>
                <PromoCodeText>{coupon.name}{" "}</PromoCodeText>
                <PromoCodeDetails>
                    ({[
                        coupon.amount_off ? `$${coupon.amount_off / 100}` : `${coupon.percent_off}%`,
                        "off",
                        coupon.duration === "repeating" ? `for ${coupon.duration_in_months} months` : coupon.duration
                    ].join(" ")})
                </PromoCodeDetails>
            </div>
        </StyledBillingPromoCoupon>
    );
};
